/**
 * Send Feedback Modal
 * Allows staff to send structured feedback to sellers/landlords
 */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { MessageSquare, Send, Loader2 } from "lucide-react";
import { API } from "@/App";

const SendFeedbackModal = ({ 
  open, 
  onOpenChange, 
  context = "sales", // "sales" | "lettings"
  propertyId = null,
  caseId = null,
  instructionId = null,
  recipientEmail,
  recipientType = "seller", // "seller" | "landlord"
  recipientName = "Client",
  onSuccess
}) => {
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const token = localStorage.getItem("token");

  const subjects = [
    { value: "Viewing Feedback", label: "Viewing Feedback" },
    { value: "Weekly Update", label: "Weekly Update" },
    { value: "General Update", label: "General Update" },
  ];

  const handleSend = async () => {
    if (!subject) {
      toast.error("Please select a subject");
      return;
    }
    if (!message.trim()) {
      toast.error("Please enter a message");
      return;
    }
    if (!recipientEmail) {
      toast.error("No recipient email available");
      return;
    }

    setSending(true);
    try {
      await axios.post(
        `${API}/feedback`,
        {
          context,
          property_id: propertyId,
          case_id: caseId,
          instruction_id: instructionId,
          recipient_email: recipientEmail,
          recipient_type: recipientType,
          subject,
          message: message.trim()
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Feedback sent to ${recipientName}`);
      setSubject("");
      setMessage("");
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error("Error sending feedback:", error);
      toast.error(error.response?.data?.detail || "Failed to send feedback");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5" />
            Send Feedback to {recipientName}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div>
            <Label>Subject</Label>
            <Select value={subject} onValueChange={setSubject}>
              <SelectTrigger data-testid="feedback-subject">
                <SelectValue placeholder="Select a subject..." />
              </SelectTrigger>
              <SelectContent>
                {subjects.map((s) => (
                  <SelectItem key={s.value} value={s.value}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Message</Label>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Enter your feedback message..."
              rows={6}
              data-testid="feedback-message"
            />
          </div>

          <p className="text-xs text-muted-foreground">
            This feedback will be visible to {recipientName} in their portal.
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSend} disabled={sending} data-testid="send-feedback-btn">
            {sending ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Send className="w-4 h-4 mr-2" />
            )}
            Send Feedback
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SendFeedbackModal;
